import { ArrowRight, Quote, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MediaFrame, ScrollReveal, SplitReveal } from '@/components/motion';
import {
  ALUMNI_HERO,
  ALUMNI_LEDE,
  ALUMNI_PILLARS,
  ALUMNI_QUOTE,
  ALUMNI_SPOTLIGHT,
  ALUMNI_TESTIMONIALS,
} from '@/data/experience';

/** Typographic initials for a monogram avatar — honorifics ("Dr.") stripped. */
function initialsOf(name: string): string {
  return name
    .replace(/\b(dr|prof|mr|mrs|ms)\.?/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * AlumniCommunity (/experience — section 5) — "A network that lasts beyond
 * graduation." A calm, credibility-first close to the Experience page: the
 * ALUMNI_LEDE under a SplitReveal headline, the ALUMNI_HERO graduate-testimonial
 * film (subtitled, muted on-view loop via MediaFrame), then ALUMNI_QUOTE as a large gold
 * pull-quote (verbatim), a three-up ScrollReveal grid of ALUMNI_PILLARS as plain
 * hover-lift cards, three attributed testimonial cards with typographic monogram
 * avatars (no stock portraits of real named people), and a gold-accented
 * ALUMNI_SPOTLIGHT card — the whole card is a router Link to the full guest-
 * speaker news story, with a verified photo pair from her campus visit and a
 * "Read the story" arrow affordance (hover-lift + gold focus ring, both
 * reduced-motion safe).
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the pillar and
 * testimonial grids — all primitives reduced-motion safe, never stranding text.
 *
 * Sourcing: every fact from '@/data/experience'; testimonial quotes reproduced
 * verbatim (speakers' own US spelling left unaltered).
 */
export function AlumniCommunity() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Alumni
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            A network that lasts beyond graduation.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {ALUMNI_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Hero film — subtitled graduate testimonial ──────────────── */}
        <div className="mt-10 md:mt-12">
          <MediaFrame
            image={ALUMNI_HERO.poster}
            video={ALUMNI_HERO.video}
            alt={ALUMNI_HERO.alt}
            aspect="aspect-video"
          />
        </div>

        {/* ── Pull-quote (verbatim, gold left-rule + quote mark) ──────── */}
        <ScrollReveal from="up" delay={0.1}>
          <figure className="mt-12 border-l-2 border-brand-gold pl-6 md:mt-14 md:pl-8">
            <Quote aria-hidden className="size-8 text-brand-gold/70 md:size-10" />
            <blockquote className="mt-4 max-w-3xl font-display text-2xl leading-snug text-foreground md:text-3xl">
              {ALUMNI_QUOTE}
            </blockquote>
          </figure>
        </ScrollReveal>

        {/* ── Pillars — plain hover-lift cards ────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-14 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3"
        >
          {ALUMNI_PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <pillar.Icon aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {pillar.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {pillar.description}
              </p>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Testimonials — attributed, monogram avatars (no photos) ─── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {ALUMNI_TESTIMONIALS.map((testimonial) => (
            <figure
              key={testimonial.name}
              className="flex h-full flex-col rounded-2xl border border-border bg-muted/30 p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <blockquote className="flex-1 text-base leading-relaxed text-foreground">
                “{testimonial.quote}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-4 border-t border-border pt-5">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy font-display text-sm font-semibold tracking-wide text-brand-gold-light"
                >
                  {initialsOf(testimonial.name)}
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-base leading-tight font-semibold text-foreground">
                    {testimonial.name}
                  </span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {testimonial.detail}
                  </span>
                </span>
              </figcaption>
            </figure>
          ))}
        </ScrollReveal>

        {/* ── Spotlight — featured guest speaker (links to the news story) ── */}
        <ScrollReveal from="up" delay={0.1}>
          <Link
            to={ALUMNI_SPOTLIGHT.href}
            className="group mt-10 flex flex-col gap-6 rounded-2xl border border-brand-gold/30 bg-brand-navy p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none md:flex-row md:items-center md:gap-8 md:p-8 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          >
            {/* Photo pair — verified shots from her guest-speaker visit. */}
            <div className="grid w-full shrink-0 grid-cols-2 gap-3 md:w-80">
              {ALUMNI_SPOTLIGHT.photos.map((photo) => (
                <img
                  key={photo.src}
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  className="aspect-[3/4] w-full rounded-xl object-cover"
                />
              ))}
            </div>
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                <Star aria-hidden className="size-4 text-brand-gold-light" />
                Alumni spotlight
              </p>
              <p className="mt-2 font-display text-xl leading-tight font-semibold text-white">
                {ALUMNI_SPOTLIGHT.name}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/70">
                {ALUMNI_SPOTLIGHT.note}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 font-label text-xs font-semibold tracking-[0.18em] text-brand-gold-light uppercase">
                {ALUMNI_SPOTLIGHT.cta}
                <ArrowRight
                  aria-hidden
                  className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                />
              </span>
            </div>
          </Link>
        </ScrollReveal>
      </div>
    </section>
  );
}
